// Estimate Engine ▸ Items ▸ Import from Excel. Paste a previous estimate's
// worksheet (header row included), review the plan, then import. Cost only —
// sales burden and markup are never imported and are CLEARED on a matched
// item (they're set per estimate). Rules + parsing: src/utils/itemImport.js.
// Saves go one row at a time through the same sp_SaveEstimateCostItem
// payload the Items form uses (utils/costLibraryPayload.js).
import { useMemo, useState } from "react";
import { sqlRead, payloadOf } from "../utils/sqlClient";
import { parseItemSheet, buildImportPlan, planRowToItem, summarizePlan } from "../utils/itemImport";
import { itemSavePayload } from "../utils/costLibraryPayload";
import { btnPrimary } from "../styles/buttons";

const th = { textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--text3)", padding: "5px 8px", borderBottom: "1px solid var(--cs-border)", whiteSpace: "nowrap", position: "sticky", top: 0, background: "var(--surface)" };
const td = { padding: "4px 8px", borderBottom: "1px solid var(--cs-border)", fontSize: 12, verticalAlign: "top" };
const ACTION_STYLE = {
  create:    { label: "New",       color: "#1a7f37" },
  update:    { label: "Update",    color: "#0b5cad" },
  unchanged: { label: "No change", color: "var(--text3)" },
  skip:      { label: "Skipped",   color: "#9a6700" },
  error:     { label: "Error",     color: "#c62828" },
  header:    { label: "Header",    color: "var(--text3)" },
  blank:     { label: "Blank",     color: "var(--text3)" },
};
const money = (v) => (v == null || v === "" ? "—" : `$${(+v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const CONCURRENCY = 4;

export default function ItemImportPanel({ items, costCodes, uoms, divisions, emptyItem, onDone, onClose }) {
  const [text, setText] = useState("");
  const [filter, setFilter] = useState("actionable"); // actionable | all
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);

  const parsed = useMemo(() => (text.trim() ? parseItemSheet(text) : null), [text]);
  const plan = useMemo(() => (parsed && !parsed.errors.length ? buildImportPlan(parsed, { items, costCodes, uoms, divisions }) : []), [parsed, items, costCodes, uoms, divisions]);
  const summary = useMemo(() => summarizePlan(plan), [plan]);
  const toSave = plan.filter(p => p.action === "create" || p.action === "update");
  const shown = filter === "all" ? plan : plan.filter(p => !["header", "blank", "unchanged"].includes(p.action));

  const ccLabel = (id) => { const c = costCodes.find(x => x.CostCodeID === id); return c ? c.CSICode : id == null ? "—" : `#${id}`; };
  const divLabel = (id) => divisions.find(d => d.CategoryLibraryID === id)?.Name || (id == null ? "—" : `#${id}`);
  const fmt = (field, v) => {
    if (field === "CostCodeID") return ccLabel(v);
    if (field === "DivisionID") return divLabel(v);
    if (field === "SalesBurdenPct" || field === "MarkupPct") return v == null ? "blank" : `${+v}%`;
    if (field === "VendorSubCostPerUnit" || field === "MaterialCostPerUnit") return money(v);
    return v == null || v === "" ? "—" : String(v);
  };

  function onFile(e) {
    const f = e.target.files?.[0]; if (!f) return;
    const rd = new FileReader(); rd.onload = () => setText(String(rd.result || "")); rd.readAsText(f);
  }

  async function runImport() {
    if (!toSave.length) return;
    const msg = `Import ${summary.create} new item(s) and update ${summary.update} existing item(s)?` +
      (summary.clearsLevers ? `\n\n${summary.clearsLevers} existing item(s) will have their sales burden / markup cleared (blank — priced by each estimate's settings).` : "") +
      (summary.noCostCode ? `\n\n${summary.noCostCode} item(s) will have no cost code.` : "") +
      "\n\nThis writes to the live catalog.";
    if (!window.confirm(msg)) return;
    setBusy(true); setResult(null);
    const failures = []; let done = 0, created = 0, updated = 0;
    setProgress({ done, total: toSave.length });
    const queue = [...toSave];
    async function worker() {
      while (queue.length) {
        const p = queue.shift();
        try {
          await sqlRead(`EXEC sp_SaveEstimateCostItem @payload = '${payloadOf(itemSavePayload(planRowToItem(p, emptyItem)))}'`);
          if (p.action === "create") created++; else updated++;
        } catch (e) { failures.push({ line: p.line, name: p.name, message: e.message || String(e) }); }
        done++; setProgress({ done, total: toSave.length });
      }
    }
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, toSave.length) }, worker));
    setBusy(false); setProgress(null);
    setResult({ created, updated, failures });
    if (!failures.length) setText("");
    onDone?.();
  }

  return (
    <div style={{ border: "1px solid var(--cs-border)", borderRadius: 8, padding: 12, marginBottom: 12, background: "var(--surface)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <strong style={{ fontSize: 13 }}>Import items from Excel</strong>
        <span onClick={onClose} style={{ cursor: "pointer", fontSize: 12, color: "var(--cs-blue)" }}>Close</span>
      </div>
      <p style={{ fontSize: 12, color: "var(--text3)", margin: "6px 0 10px" }}>
        Copy the estimate sheet from Excel <strong>including its header row</strong> and paste it below (or upload a CSV).
        Imported: Cost Code, Description, UOM, Subcontract Unit Cost (plus Material Unit Cost / Labor Hr/Unit / Code / Division if present); the Division
        also comes from a header row like "Civil". <strong>Cost only</strong> — Sales Burden and Markup are never imported, and an existing item that matches
        has them <strong>cleared</strong> so each estimate's own settings price it. Qty, totals, Unit Cost, Cost/SF and Zone are ignored.
        Rows match existing items by Code, else by name; nothing is deleted. Cost codes must already be corrected on the sheet — one that isn't in Cost Codes
        imports with none (fix it afterwards in the "No Cost Code" group).
      </p>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
        <textarea value={text} onChange={e => { setText(e.target.value); setResult(null); }} disabled={busy} placeholder="Paste the sheet here (tab-separated from Excel, or CSV)…"
          style={{ width: 620, height: 120, fontFamily: "monospace", fontSize: 11, padding: 6, border: "1px solid var(--cs-border)", borderRadius: 4 }} />
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <input type="file" accept=".csv,.tsv,.txt" onChange={onFile} disabled={busy} style={{ fontSize: 12 }} />
          <button onClick={runImport} disabled={busy || !toSave.length} style={btnPrimary}>
            {busy && progress ? `Saving ${progress.done} / ${progress.total}…` : `Import ${toSave.length} item${toSave.length === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>

      {parsed?.errors?.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 12, color: "#c62828" }}>{parsed.errors.map((e, i) => <div key={i}>Line {e.line}: {e.message}</div>)}</div>
      )}
      {result && (
        <div style={{ marginTop: 8, fontSize: 12 }}>
          <span style={{ color: "#1a7f37" }}>Saved: {result.created} new, {result.updated} updated.</span>
          {result.failures.length > 0 && (
            <div style={{ color: "#c62828" }}>
              {result.failures.length} failed — the rest were saved:
              {result.failures.map(f => <div key={f.line}>Line {f.line} ({f.name}): {f.message}</div>)}
            </div>
          )}
        </div>
      )}

      {parsed && !parsed.errors.length && (
        <>
          <div style={{ marginTop: 10, fontSize: 12, display: "flex", gap: 14, flexWrap: "wrap" }}>
            <span>Columns used: {parsed.header.mapped.map(m => m.header).join(", ") || "—"}</span>
            {parsed.header.ignored.length > 0 && <span style={{ color: "var(--text3)" }}>Ignored: {parsed.header.ignored.map(m => m.header).join(", ")}</span>}
            {parsed.header.unmapped.length > 0 && <span style={{ color: "#9a6700" }}>Not recognised: {parsed.header.unmapped.join(", ")}</span>}
          </div>
          <div style={{ marginTop: 6, fontSize: 12, display: "flex", gap: 14, flexWrap: "wrap" }}>
            <strong>{summary.create} new · {summary.update} update · {summary.unchanged} no change · {summary.skip} skipped · {summary.error} errors</strong>
            {summary.clearsLevers > 0 && <span>{summary.clearsLevers} updates clear burden / markup</span>}
            {summary.noCostCode > 0 && <span style={{ color: "#9a6700" }}>{summary.noCostCode} without a cost code</span>}
            <label style={{ marginLeft: "auto" }}><input type="checkbox" checked={filter === "all"} onChange={e => setFilter(e.target.checked ? "all" : "actionable")} /> show headers / blank / unchanged rows</label>
          </div>
          <div style={{ marginTop: 6, maxHeight: 420, overflow: "auto", border: "1px solid var(--cs-border)", borderRadius: 6 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={th}>Line</th><th style={th}>Action</th><th style={th}>Item</th><th style={th}>UOM</th><th style={th}>Cost code</th><th style={th}>Division</th>
                <th style={{ ...th, textAlign: "right" }}>Vendor/sub $</th><th style={th}>Changes / notes</th>
              </tr></thead>
              <tbody>
                {shown.map(p => {
                  const a = ACTION_STYLE[p.action] || { label: p.action, color: "inherit" };
                  if (p.action === "header" || p.action === "blank") {
                    return <tr key={p.line}><td style={td}>{p.line}</td><td style={{ ...td, color: a.color }}>{a.label}</td><td style={{ ...td, color: "var(--text3)" }} colSpan={6}>{p.label || ""}</td></tr>;
                  }
                  const notes = [
                    ...(p.errors || []).map(m => ({ m, c: "#c62828" })),
                    ...(p.reason ? [{ m: p.reason, c: "#9a6700" }] : []),
                    ...(p.warnings || []).map(m => ({ m, c: "#9a6700" })),
                  ];
                  return (
                    <tr key={p.line}>
                      <td style={td}>{p.line}</td>
                      <td style={{ ...td, color: a.color, fontWeight: 600 }}>{a.label}{p.existing ? <div style={{ fontWeight: 400, fontSize: 11, color: "var(--text3)" }}>item #{p.existing.ItemID}</div> : null}</td>
                      <td style={td}>{p.name}</td>
                      <td style={td}>{p.uom || "—"}</td>
                      <td style={td}>{p.next ? ccLabel(p.next.CostCodeID) : p.costCode?.CSICode || "—"}</td>
                      <td style={td}>{p.next ? divLabel(p.next.DivisionID) : p.division?.Name || "—"}</td>
                      <td style={{ ...td, textAlign: "right" }}>{money(p.costs?.vendor)}</td>
                      <td style={td}>
                        {p.action === "update" && p.changes.map(ch => (
                          <div key={ch.field}>{ch.label}: <span style={{ color: "var(--text3)", textDecoration: "line-through" }}>{fmt(ch.field, ch.from)}</span> → {fmt(ch.field, ch.to)}</div>
                        ))}
                        {notes.map((n, i) => <div key={i} style={{ color: n.c }}>{n.m}</div>)}
                      </td>
                    </tr>
                  );
                })}
                {!shown.length && <tr><td style={td} colSpan={8}>Nothing to import.</td></tr>}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 11, color: "var(--text3)", margin: "6px 0 0" }}>
            Check this preview against the source sheet before importing — the estimator is responsible for the costs that go into the catalog.
          </p>
        </>
      )}
    </div>
  );
}
